import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { useAuth } from '../../context/AuthContext';
import { updateProfile, uploadProfilePhoto, deleteProfilePhoto } from '../../lib/api';
import { toast } from 'sonner';
import { Camera, Loader2, Trash2, User } from 'lucide-react';

export default function ProfileSettings() {
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), bio: bio.trim() || null });
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please select a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      await uploadProfilePhoto(file);
      await refreshUser();
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async () => {
    if (!user?.profile_photo_url) return;

    setDeletingPhoto(true);
    try {
      await deleteProfilePhoto();
      await refreshUser();
      toast.success('Profile photo removed');
    } catch (error) {
      console.error('Failed to delete photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  const getInitials = () => {
    return user?.name?.charAt(0).toUpperCase() || 'U';
  };

  const getPhotoUrl = () => {
    if (!user?.profile_photo_url) return null;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${user.profile_photo_url}`;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Profile Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account information</p>
      </div>

      {/* Profile Photo Section */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Photo</CardTitle>
          <CardDescription>
            Upload a photo to personalize your profile
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <Avatar className="w-24 h-24">
              {user?.profile_photo_url ? (
                <AvatarImage src={getPhotoUrl()} alt={user?.name} />
              ) : null}
              <AvatarFallback className="text-2xl bg-primary text-white">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="photo-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {user?.profile_photo_url ? 'Change Photo' : 'Upload Photo'}
              </Button>
              
              {user?.profile_photo_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={handleDeletePhoto}
                  disabled={deletingPhoto}
                  data-testid="delete-photo-btn"
                >
                  {deletingPhoto ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Remove Photo
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            Accepted formats: JPEG, PNG, WebP. Max size: 5MB.
          </p>
        </CardContent>
      </Card>

      {/* Profile Info Section */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>
            Update your display name and bio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-500">
                Email cannot be changed
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                maxLength={100}
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell others a bit about yourself..."
                rows={3}
                maxLength={500}
                data-testid="bio-input"
              />
              <p className="text-xs text-slate-500 text-right">
                {bio.length}/500
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Account Type</span>
            <span className="font-medium capitalize">{user?.role}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Member Since</span>
            <span className="font-medium">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              }) : '-'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
